import { FC } from 'react';

import { parse, DATE_FORMAT } from '@eneco/common/date';
import { useFormatter, useTranslation } from '@eneco/i18n';
import { InputDatepicker, NotificationBox, Stack, Text } from '@eneco/sparky';
import { Control, Controller, FieldErrors } from 'react-hook-form';

import { DeliveryDateFormFieldsB } from '../utils/deliveryDateOptionSchema_B';

interface ContractEndCardBodyProps {
  control: Control<DeliveryDateFormFieldsB>;
  errors: FieldErrors<DeliveryDateFormFieldsB>;
  fromDate: Date;
  toDate: Date;
  /** The entered end date falls before the earliest date we can start delivering. */
  shouldShowEarlyEndDateNotification: boolean;
  onDayClick: () => void;
}

export const ContractEndCardBody: FC<ContractEndCardBodyProps> = ({
  control,
  errors,
  fromDate,
  toDate,
  shouldShowEarlyEndDateNotification,
  onDayClick,
}) => {
  const { date } = useFormatter();
  const { t } = useTranslation();

  return (
    <Stack gap="3">
      <Text size="BodyS">{t('contractEnd.body')}</Text>

      <Controller
        control={control}
        name="contractEndDate"
        render={({ field: { onChange, value, name } }) => (
          <InputDatepicker
            name={name}
            label={t('contractEnd.dateLabel')}
            hint={t('contractEnd.dateHint')}
            error={errors.contractEndDate?.message}
            value={value}
            onDaySelect={d => onChange(d && date.short(d as Date))}
            onDayClick={onDayClick}
            fromDate={fromDate}
            toDate={toDate}
            defaultMonth={value ? parse(value, DATE_FORMAT) : undefined}
            isReadOnly
          />
        )}
      />

      {shouldShowEarlyEndDateNotification && (
        <Text size="BodyS">
          <NotificationBox
            isAlert={false}
            variant="info"
            title={t('contractEnd.earlyEndDateTitle')}
            text={t('contractEnd.earlyEndDateText')}
          />
        </Text>
      )}
    </Stack>
  );
};
